import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import Modal from 'features/app/components/Modal';
import BaseButton from 'features/app/components/BaseButton';
import BaseBalanceDropdown from 'features/app/components/BaseBalanceDropdown';
import { InputField } from 'shared/forms';
import { ShareBalanceStep } from 'features/users/components/ShareBalanceStep';

import { useMessage } from 'features/message/hooks/useMessage';
import { usePartnerBalanceQuery } from 'features/app/hooks/usePartnerBalanceQuery';
import { useTranslation } from 'hooks/useTranslation';
import useValidation from 'hooks/useValidation';
import { useStyles } from 'features/users/components/ShareBalanceModal/styles';

import type { BalanceCurrency } from 'features/app/ts/BalanceCurrency';
import { ShareBalance } from 'features/users/ts/ShareBalance';

import { shareBalanceRequest } from 'features/users/api';
import { getBalanceCurrencyId } from 'utils/localStorage';
import { parseFieldErrors } from 'utils/error-utils';
import { ErrorUtils } from 'utils';

const DEFAULT_VALUES: Partial<ShareBalance> = {
  amount: '',
};

type ShareBalanceModalProps = {
  onSubmit?: () => Promise<unknown> | void;
  onClose: () => void;
  open: boolean;
  id: number;
};

export const ShareBalanceModal: React.FC<ShareBalanceModalProps> = ({ onSubmit: onModalSubmit, onClose, open, id }) => {
  const classes = useStyles();
  const { t } = useTranslation('users');
  const [displayedBalanceId, setDisplayedBalanceId] = useState(() => getBalanceCurrencyId());
  const { data, isLoading, error } = usePartnerBalanceQuery();
  const { required, minNumber, maxNumber } = useValidation();
  const { showErrorMessage, showSuccessMessage } = useMessage();

  const [step, setStep] = useState('amount');
  const [currencyCode, setCurrencyCode] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);

  const options = React.useMemo(
    () => (data ? data.filter(item => item.id !== displayedBalanceId) : []),
    [data, displayedBalanceId]
  );

  const displayedBalance = React.useMemo(
    () => data?.find(item => item.id === displayedBalanceId) ?? data?.[0],
    [displayedBalanceId, data]
  );

  useEffect(() => {
    displayedBalance && setCurrencyCode(displayedBalance.currency_code);
  }, [displayedBalance]);

  const form = useForm<ShareBalance>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = form;

  const handleChangeCurrency = (item: BalanceCurrency) => {
    setDisplayedBalanceId(item.id);
    setCurrencyCode(item.currency_code);
  };

  const handleCheckAmount = (values: ShareBalance) => {
    setAmount(values.amount);
    setStep('share');
  };

  const handleShareBalance = async () => {
    try {
      if (currencyCode && amount) {
        await shareBalanceRequest({
          transferred_to_user: id,
          currency: currencyCode,
          amount,
        });
        await onModalSubmit?.();
        showSuccessMessage(t('shareBalance.balanceShared'));
        onClose();
      }
    } catch (err) {
      const parsedError = ErrorUtils.convertError(err);
      parseFieldErrors<ShareBalance>(parsedError).forEach(({ field, ...error }) => setError(field, error));
      showErrorMessage(parsedError);
    }
  };

  return (
    <Modal onClose={onClose} open={open} paperClass={classes.paper} dialogContentClass={classes.dialogContent}>
      {step === 'amount' && (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(handleCheckAmount)}>
            <Box className={classes.balance}>
              <Typography variant="h1" className={classes.heading}>
                {t('enterAmountStep.heading1')}
              </Typography>

              {error ? (
                <Typography className={classes.error}>{t('enterAmountStep.errorText')}</Typography>
              ) : isLoading ? (
                <CircularProgress size={50} />
              ) : (
                <Box className={classes.fieldWrapper}>
                  <BaseBalanceDropdown
                    displayedBalance={displayedBalance}
                    onChange={handleChangeCurrency}
                    options={options}
                  />
                </Box>
              )}
            </Box>

            <Box className={classes.amount}>
              <Typography variant="h2" className={classes.heading}>
                {t('enterAmountStep.heading2')}
              </Typography>

              <Box className={classes.fieldWrapper}>
                <InputField
                  name="amount"
                  fullWidth
                  placeholder={t('enterAmountStep.enterAmount')}
                  className={classes.inputField}
                  required
                  type="text"
                  rules={{
                    validate: {
                      ...required,
                      ...minNumber(1),
                      ...maxNumber(Number(displayedBalance?.value)),
                      // isInteger,
                    },
                  }}
                />
              </Box>
            </Box>

            <BaseButton
              className={classes.button}
              labelClass={classes.buttonLabel}
              disabled={Boolean(error)}
              isLoading={isSubmitting}
              type="submit"
            >
              {t('shareBalance.amountStepButton')}
            </BaseButton>
          </form>
        </FormProvider>
      )}

      {step === 'share' && currencyCode && (
        <ShareBalanceStep
          availableBalance={Number(displayedBalance?.value)}
          currency={currencyCode}
          amount={Number(amount)}
          handleSumbit={handleShareBalance}
        />
      )}
    </Modal>
  );
};
